import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { Badge } from '../../../ui/badge';
import { ScrollArea } from '../../../ui/scroll-area';
import { Button } from '../../../ui/button';
import { Textarea } from '../../../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';
import { Info, AlertCircle, TrendingUp, Calendar, Plus, Trash2, Edit3, Save, X } from 'lucide-react';
import { ShiftPatternData } from '@shiftly/shared/src/calculators/staff_report/staffReportCalculator';
import { StaffReportUserNotes } from '../../../../lib/supabase/staffReport/supabaseStaffReportService';

interface UserInspectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  user_id: string;
  shiftPatternData: ShiftPatternData | null;
  userNotes: StaffReportUserNotes | null;
  modelName: string;
  onAddStrike?: (user_id: string, reason: string) => Promise<void>;
  onRemoveStrike?: (user_id: string, strikeField: 'strike1' | 'strike2' | 'strike3') => Promise<void>;
  onUpdateNotes?: (user_id: string, notes: string) => Promise<void>;
}

const STRIKE_REASONS = [
  { value: 'Laziness', label: 'Laziness' },
  { value: 'Sexting quality', label: 'Sexting quality' },
  { value: 'No smart messages', label: 'No smart messages' },
  { value: 'Not unsending mass dms', label: 'Not unsending mass dms' },
  { value: 'Other (write in notes)', label: 'Other (write in notes)' }
];

export function UserInspectionDialog({
  isOpen,
  onClose,
  userName,
  user_id,
  shiftPatternData,
  userNotes,
  modelName,
  onAddStrike,
  onRemoveStrike,
  onUpdateNotes
}: UserInspectionDialogProps) {
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [isAddingStrike, setIsAddingStrike] = useState(false);
  const [selectedStrikeReason, setSelectedStrikeReason] = useState('');
  const strikeCount = useMemo(() => {
    if (!userNotes) return 0;
    return [userNotes.strike1, userNotes.strike2, userNotes.strike3]
      .filter(strike => strike && strike.trim() !== '').length;
  }, [userNotes]);

  const strikeColorClass = useMemo(() => {
    if (strikeCount === 0) return 'bg-brand-green text-brand-dark border-brand-green';
    if (strikeCount === 1) return 'bg-brand-gold text-brand-dark border-brand-gold';
    if (strikeCount === 2) return 'bg-brand-gold text-brand-dark border-brand-gold';
    return 'bg-brand-red text-brand-dark border-brand-red';
  }, [strikeCount]);

  const handleStartEditNotes = () => {
    setEditedNotes(userNotes?.notes || '');
    setIsEditingNotes(true);
  };

  const handleSaveNotes = async () => {
    if (onUpdateNotes) {
      try {
        await onUpdateNotes(user_id, editedNotes);
        setIsEditingNotes(false);
      } catch (error) {
        console.error('Error updating notes:', error);
      }
    }
  };

  const handleCancelEditNotes = () => {
    setIsEditingNotes(false);
    setEditedNotes('');
  };

  const handleAddStrike = async () => {
    if (onAddStrike && selectedStrikeReason) {
      try {
        await onAddStrike(user_id, selectedStrikeReason);
        setIsAddingStrike(false);
        setSelectedStrikeReason('');
      } catch (error) {
        console.error('Error adding strike:', error);
      }
    }
  };

  const handleRemoveStrike = async (strikeField: 'strike1' | 'strike2' | 'strike3') => {
    if (onRemoveStrike) {
      try {
        await onRemoveStrike(user_id, strikeField);
      } catch (error) {
        console.error('Error removing strike:', error);
      }
    }
  };

  const getStrikeField = (index: number): 'strike1' | 'strike2' | 'strike3' => {
    return `strike${index + 1}` as 'strike1' | 'strike2' | 'strike3';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-brand-dark dark:text-foreground flex items-center gap-2">
            <Info className="h-5 w-5 text-muted-foreground" />
            User Inspection
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[600px] pr-4">
          <div className="space-y-6 pb-4">
            {/* User Info Header */}
            <div className="border rounded-lg p-4 dark:border-brand-border">
              <h2 className="text-lg font-semibold mb-3">{userName}</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground dark:text-muted-foreground mb-1">Model</p>
                  <p className="font-medium text-brand-dark dark:text-foreground">{modelName || 'Not assigned'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground dark:text-muted-foreground mb-1">Shift Pattern</p>
                  <p className="font-medium text-brand-dark dark:text-foreground">{shiftPatternData?.shiftPattern || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Performance Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold text-brand-dark dark:text-foreground">Performance Averages</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="border rounded-lg p-4 dark:border-brand-border bg-brand-dark dark:bg-brand-dark/50">
                  <p className="text-sm text-muted-foreground dark:text-muted-foreground mb-2">PPV per Shift</p>
                  <p className="text-2xl font-bold text-brand-dark dark:text-foreground">
                    ${shiftPatternData?.dollarPerShift.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="border rounded-lg p-4 dark:border-brand-border bg-brand-dark dark:bg-brand-dark/50">
                  <p className="text-sm text-muted-foreground dark:text-muted-foreground mb-2">Non-PPV per Shift</p>
                  <p className="text-2xl font-bold text-brand-dark dark:text-foreground">
                    ${shiftPatternData?.noppvPerShift.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
            </div>

            {/* Strikes Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-semibold text-brand-dark dark:text-foreground">Strike History</h3>
                </div>
                <Badge className={`${strikeColorClass} border`}>
                  {strikeCount}/3 Strikes
                </Badge>
              </div>

              <div className="space-y-2">
                {[userNotes?.strike1, userNotes?.strike2, userNotes?.strike3].map((strike, index) => {
                  const hasStrike = strike && strike.trim() !== '';
                  const canAddStrike = !hasStrike && onAddStrike && strikeCount < 3;
                  
                  if (hasStrike) {
                    // Existing strike card
                    return (
                      <div key={index} className="border rounded-lg p-3 dark:border-brand-border bg-brand-red dark:bg-brand-dark/20">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1">
                            <Badge variant="destructive" className="mt-0.5">{index + 1}</Badge>
                            <p className="text-sm text-foreground dark:text-muted-foreground flex-1">{strike}</p>
                          </div>
                          {onRemoveStrike && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRemoveStrike(getStrikeField(index))}
                              className="h-6 w-6 p-0 text-brand-red hover:text-brand-red hover:bg-brand-red"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  }
                  
                  if (canAddStrike && index === strikeCount) {
                    // Add strike template card (only show for the next available slot)
                    return (
                      <div key={`add-${index}`}>
                        {isAddingStrike ? (
                          // Add Strike Form
                          <div className="border-2 border-dashed border-brand-gold rounded-lg p-4 dark:border-brand-gold bg-brand-gold dark:bg-brand-dark/20">
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <Select value={selectedStrikeReason} onValueChange={setSelectedStrikeReason}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select strike reason" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {STRIKE_REASONS.map((reason) => (
                                      <SelectItem key={reason.value} value={reason.value}>
                                        {reason.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setIsAddingStrike(false);
                                  setSelectedStrikeReason('');
                                }}
                                className="px-2"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={handleAddStrike}
                                disabled={!selectedStrikeReason}
                                className="px-2"
                              >
                                <Save className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // Dashed template card
                          <div 
                            className="border-2 border-dashed border-brand-border dark:border-brand-border rounded-lg p-3 cursor-pointer hover:border-brand-border dark:hover:border-brand-border hover:bg-brand-dark dark:hover:bg-brand-dark/50 transition-colors"
                            onClick={() => setIsAddingStrike(true)}
                          >
                            <div className="flex items-center gap-3">
                              <Plus className="h-4 w-4 text-muted-foreground dark:text-muted-foreground" />
                              <p className="text-sm text-muted-foreground dark:text-muted-foreground">Add strike {index + 1}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  return null;
                })}
                
                {strikeCount === 0 && !onAddStrike && (
                  <div className="border rounded-lg p-4 dark:border-brand-border bg-brand-dark dark:bg-brand-dark/50">
                    <p className="text-sm text-muted-foreground dark:text-muted-foreground text-center">No strikes recorded</p>
                  </div>
                )}
              </div>
            </div>

            {/* Notes Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-semibold text-brand-dark dark:text-foreground">Notes</h3>
                </div>
                {onUpdateNotes && !isEditingNotes && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleStartEditNotes}
                    className="h-7"
                  >
                    <Edit3 className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                )}
              </div>
              
              {isEditingNotes ? (
                <div className="space-y-3">
                  <Textarea
                    value={editedNotes}
                    onChange={(e) => setEditedNotes(e.target.value)}
                    placeholder="Enter notes..."
                    className="min-h-[120px] resize-none"
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelEditNotes}
                    >
                      <X className="h-3 w-3 mr-1" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveNotes}
                    >
                      <Save className="h-3 w-3 mr-1" />
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {userNotes?.notes ? (
                    <div className="border rounded-lg p-4 dark:border-brand-border bg-brand-dark dark:bg-brand-dark/50">
                      <p className="text-sm text-foreground dark:text-muted-foreground whitespace-pre-wrap">{userNotes.notes}</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg p-4 dark:border-brand-border bg-brand-dark dark:bg-brand-dark/50">
                      <p className="text-sm text-muted-foreground dark:text-muted-foreground text-center">No notes recorded</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </ScrollArea>

        <div className="flex justify-end mt-4 pt-4 border-t dark:border-brand-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}